import type { OAuthSession } from '@atproto/oauth-client-browser'
import { type MastodonSession, getMastodonAccountId } from '@/lib/mastodon'
import type { PixelfedSession } from '@/lib/pixelfed'
import type { NostrSession } from '@/types/nostr'
import type { ConnectedAccount, ExpiredAccount } from '@/types/account'
import { PROFILE_CACHE_PREFIX } from '@/constants/profileCache'
import { nip19 } from 'nostr-tools'

export interface CachedBlueskyProfile {
    displayName?: string
    handle?: string
    avatar?: string
}

/** Read the cached profile for a Bluesky DID, or null if unavailable/corrupt. */
export function readBlueskyProfileCache(did: string): CachedBlueskyProfile | null {
    try {
        const cached = localStorage.getItem(`${PROFILE_CACHE_PREFIX}bluesky:${did}`)
        if (!cached) return null
        return JSON.parse(cached) as CachedBlueskyProfile
    } catch {
        // Corrupt cache or localStorage unavailable
        return null
    }
}

export function buildBlueskyAccount(session: OAuthSession): ConnectedAccount {
    const did = session.did
    const profile = readBlueskyProfileCache(did)

    return {
        id: did,
        platform: 'bluesky',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty cached values should fall back to the DID
        displayName: profile?.displayName || did,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty cached values should fall back to the DID
        handle: profile?.handle || did,
        avatar: profile?.avatar,
    }
}

/** Build the switcher entry for an expired (signed-out) Bluesky account. */
export function buildExpiredBlueskyAccount(account: ExpiredAccount): ConnectedAccount {
    return {
        id: account.did,
        platform: 'bluesky',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty values should fall back to the handle/DID
        displayName: account.displayName || account.handle || account.did,
        handle: account.handle || account.did,
        avatar: account.avatar,
        status: 'expired',
    }
}

/** Reconstruct an expired account's identity from the profile cache. */
export function buildExpiredAccountFromCache(did: string): ExpiredAccount {
    const profile = readBlueskyProfileCache(did)
    return {
        did,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty cached values should fall back to the DID
        handle: profile?.handle || did,
        displayName: profile?.displayName,
        avatar: profile?.avatar,
    }
}

export function buildMastodonAccount(session: MastodonSession): ConnectedAccount {
    const { account, instance_url } = session
    const instanceHost = instance_url.replace(/^https?:\/\//, '').replace(/\/$/, '')

    return {
        id: getMastodonAccountId(session),
        platform: 'mastodon',
        displayName: account.display_name || account.username,
        handle: `${account.username}@${instanceHost}`,
        avatar: account.avatar,
    }
}

export function buildPixelfedAccount(session: PixelfedSession): ConnectedAccount {
    const { account, instance_url } = session
    const instanceHost = instance_url.replace(/^https?:\/\//, '').replace(/\/$/, '')

    return {
        id: `pixelfed:${instanceHost}:${account.username}`,
        platform: 'pixelfed',
        displayName: account.display_name || account.username,
        handle: `${account.username}@${instanceHost}`,
        avatar: account.avatar,
    }
}

export function buildNostrAccount(session: NostrSession): ConnectedAccount {
    const npub = nip19.npubEncode(session.pubkey)
    return {
        id: `nostr:${session.pubkey}`,
        platform: 'nostr',
        displayName: session.displayName ?? npub,
        handle: npub,
        avatar: session.avatar,
    }
}
